import React, { useEffect, useState } from 'react';

/**
 * StickyHeader — 상단 앱바 / PC GNB.
 *
 * v2(2026-09-21): 모바일 목업 .appbar 56px(스크롤 시 48px) · PC(md+) 목업 .gnb 68px 고정.
 * 불투명 흰색 배경 + 오렌지 브랜드 포인트. 이미지 위에서도 글자 대비를 유지한다.
 *
 * RN 호환: window.scrollY는 web에서만, RN에서는 ScrollView onScroll로 prop 주입.
 */

export interface StickyHeaderProps {
  /** 좌측 — 로고 또는 뒤로가기 */
  left?: React.ReactNode;
  /** 중앙 — 페이지 제목 (선택, 스크롤 시 등장 가능) */
  center?: React.ReactNode;
  /** 우측 — 아이콘 버튼들 */
  right?: React.ReactNode;
  /** 스크롤 임계 (px) — 이 이상이면 compact 모드 */
  shrinkAt?: number;
  /** 외부 제어 (RN 호환): 직접 compact 모드 강제 */
  forceCompact?: boolean;
  /**
   * 중앙 슬롯 표시 모드 (기본 'title')
   *   - 'title' : 페이지 제목용. compact 모드일 때만 opacity 1, 평소엔 숨김
   *   - 'always': 검색바 등 항상 보여야 하는 요소용. opacity 토글 없음
   */
  centerMode?: 'title' | 'always';
  className?: string;
}

export const StickyHeader: React.FC<StickyHeaderProps> = ({
  left,
  center,
  right,
  shrinkAt = 24,
  forceCompact,
  centerMode = 'title',
  className = '',
}) => {
  const [compact, setCompact] = useState<boolean>(!!forceCompact);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (forceCompact !== undefined) {
      setCompact(forceCompact);
      return;
    }
    const onScroll = () => setCompact(window.scrollY > shrinkAt);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shrinkAt, forceCompact]);

  return (
    <header
      className={`brand-app-header sticky top-0 z-20 flex items-center justify-between gap-2 bg-frame text-ink px-3 md:h-[68px] md:gap-5 md:px-6 lg:px-8 ${
        compact ? 'h-[48px]' : 'h-[56px]'
      } ${className}`}
    >
      <div className="brand-app-header__left flex min-w-0 items-center gap-2">{left}</div>
      {center && centerMode === 'title' && (
        <div
          className={`min-w-0 flex-1 px-2 text-center text-[14px] font-semibold text-ink truncate transition-opacity ${
            compact ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {center}
        </div>
      )}
      {center && centerMode === 'always' && (
        <div className="min-w-0 flex-1 px-2">{center}</div>
      )}
      <div className="brand-app-header__actions flex items-center gap-1">{right}</div>
    </header>
  );
};

export default StickyHeader;
